// Node and edge styling utilities for graph visualization

// Complementary color palette designed to work harmoniously with brand green (#A0EF6A)
const BRAND_COLORS = {
  // Primary brand green spectrum
  primary: '#A0EF6A',        // Main brand color
  primaryDark: '#86efac',     // Darker brand variant
  primaryLight: '#bbf7d0',    // Lighter brand variant
  
  // Complementary colors (based on color theory)
  coral: '#FF8A65',          // Warm coral (complementary to green)
  lavender: '#B39DDB',       // Soft lavender purple
  sage: '#81C784',           // Sage green (analogous)
  terracotta: '#FFAB91',     // Warm terracotta
  slate: '#78909C',          // Cool slate blue-gray
  
  // Accent colors for visibility states
  red: '#DC2626',            // Red for private
  grey: '#9CA3AF',           // Light grey for ask me
  amber: '#FFD54F',          // Warm amber for highlights
};

/**
 * Get node style based on type and properties
 * @param {Object} node - Node object with type and content properties
 * @returns {Object} Style object with size, color, and labelSize
 */
export const getNodeStyle = (node) => {
  let size, labelSize;
  
  // Set size and label size based on node type
  if (node.isHub) {
    size = 30; // Larger hubs for better visibility
    labelSize = 16; // Larger labels for hubs
  } else if (node.isCluster) {
    size = 20; // Medium clusters
    labelSize = 14; // Medium labels for clusters
  } else if (node.isTopicArea) {
    size = 15; // Topic areas between clusters and conversations
    labelSize = 13; // Medium-small labels for topic areas
  } else {
    size = Math.max(8, Math.min(15, (node.content?.length || 50) / 10)); // Variable size for conversations
    labelSize = 12; // Standard label size
  }
  
  // All nodes use visibility-based colors
  let color;
  switch (node.visibility) {
    case 'public':
      color = BRAND_COLORS.primary; // Brand color for public nodes
      break;
    case 'private':
      color = BRAND_COLORS.red; // Red for private
      break;
    case 'ask me':
    default:
      color = BRAND_COLORS.grey; // Grey for ask me
      break;
  }
  
  return { size, color, labelSize };
};

/**
 * Get edge style based on edge type
 * @param {Object} edge - Edge object with type property
 * @returns {Object} Style object with size, color, and type
 */
export const getEdgeStyle = (edge) => {
  switch (edge.type) {
    case 'hierarchy':
      return {
        size: 1.2, // Slightly thicker for hierarchy
        color: '#81C784', // Darker green - more visible connection to brand
        type: 'line'
      };
    case 'semantic':
      return {
        size: 0.8, // Medium thickness
        color: '#9575CD', // Darker lavender - more visible while complementing node colors
        type: 'line'
      };
    case 'cross-topic':
      return {
        size: 0.6,
        color: '#FF8A65', // Darker coral - more visible warm tone
        type: 'line'
      };
    default:
      return {
        size: 0.5,
        color: '#A5D6A7', // Darker sage green - more visible while harmonious with brand
        type: 'line'
      };
  }
};

/**
 * Get edge weight for force-directed layout
 * @param {string} edgeType - Type of edge (hierarchy, semantic, cross-topic)
 * @returns {number} Weight value for layout algorithm
 */
export const getEdgeWeight = (edgeType) => {
  switch (edgeType) {
    case 'hierarchy':
      return 5; // Strong connection for hierarchical relationships
    case 'semantic':
      return 2; // Medium connection for related topics
    case 'cross-topic':
      return 0.5; // Weak connection for cross-topic relationships
    default:
      return 1;
  }
};

/**
 * Create node reducer function for controlling labels based on zoom level
 * @param {Object} graph - Graphology graph instance
 * @param {Object} sigma - Sigma.js instance (needed for camera access)
 * @returns {Function} Node reducer function for Sigma.js
 */
export const createNodeReducer = (graph, sigma) => {
  return (node, data) => {
    const nodeData = graph.getNodeAttributes(node);
    const newData = { ...data };
    
    // ALWAYS SHOW LABEL WHEN HIGHLIGHTED (HOVERED) - HIGHEST PRIORITY
    if (nodeData.highlighted) {
      newData.forceLabel = true;
      newData.label = nodeData.label || nodeData.id;
      newData.labelColor = '#000';
      newData.color = nodeData.color;
      
      // Apply pill shape styling for hovered nodes
      if (nodeData.hoverState?.pillEffect) {
        newData.borderSize = nodeData.borderSize || 3;
        newData.borderColor = nodeData.borderColor || '#ffffff';
        // Remove any shadow effects by ensuring clean circular shape
        newData.type = 'circle'; // Ensure circular shape for pill effect
      }
      
      return newData; // Return immediately to prevent zoom logic from overriding
    }
    
    // Get current camera ratio (zoom level) for non-highlighted nodes
    const camera = sigma.getCamera();
    const zoomRatio = camera.ratio;
    
    // Show labels based on zoom level for non-highlighted nodes
    if (zoomRatio > 3) {
      // Very zoomed out - show only hub labels
      if (nodeData.isHub) {
        newData.forceLabel = true;
        newData.label = nodeData.label;
        newData.labelColor = '#000';
      } else {
        newData.label = '';
      }
    } else if (zoomRatio > 1.5) {
      // Medium-far zoom - show hub and cluster labels
      if (nodeData.isHub || nodeData.isCluster) {
        newData.forceLabel = true;
        newData.label = nodeData.label;
        newData.labelColor = '#000';
      } else {
        newData.label = '';
      }
    } else if (zoomRatio > 0.3) {
      // Medium zoom - show hub, cluster, and topic area labels
      if (nodeData.isHub || nodeData.isCluster || nodeData.isTopicArea) {
        newData.forceLabel = true;
        newData.label = nodeData.label;
        newData.labelColor = '#000';
      } else {
        newData.label = '';
      }
    } else {
      // Very zoomed in - show all labels only when extremely close
      newData.forceLabel = true;
      newData.label = nodeData.label;
      newData.labelColor = '#000';
    }
    
    return newData;
  };
};

/**
 * Sigma.js configuration settings optimized for pill-shaped hover effects
 */
export const getSigmaConfig = () => ({
  // Camera settings
  minCameraRatio: 0.1,
  maxCameraRatio: 10,
  
  // Label settings - prevent overcrowding
  labelDensity: 1, // Higher value = more selective about showing labels
  labelGridCellSize: 60, // Larger grid = more spacing between labels
  labelRenderedSizeThreshold: 3, // Only show labels for reasonably sized nodes
  labelSize: 12, // Slightly smaller labels to reduce overlap
  labelFont: 'Inter',
  labelWeight: '500', // Medium weight for Inter
  
  // PROPER SIGMA.JS LABEL COLOR SETTINGS
  defaultLabelColor: '#000',
  labelHoverColor: 'default',
  defaultLabelHoverColor: '#000',
  labelHoverBGColor: 'default',
  defaultHoverLabelBGColor: 'rgba(255, 255, 255, 0.9)', // Slightly more opaque for pill effect
  
  // Node settings - optimized for pill shape hover
  defaultNodeType: 'circle', // Ensure all nodes are circular for consistent pill effect
  nodeHoverColor: 'node', // Use the node's own color instead of override
  
  // Border settings for pill effect
  defaultNodeBorderColor: '#fff',
  defaultNodeBorderSize: 2,
  
  // Disable any shadow or drop shadow effects
  nodeShadowColor: 'transparent',
  nodeShadowSize: 0,
  
  // Edge settings
  renderEdgeLabels: false,
  defaultEdgeColor: '#A5D6A7', // Darker sage green - more visible while harmonious with brand
  
  // Performance
  hideEdgesOnMove: false,
  hideLabelsOnMove: false,
  zIndex: true,
  
  // Rendering optimizations for smooth hover effects
  enableEdgeHoverEvents: false, // Disable edge hover for better performance
  enableNodeHoverEvents: true // Ensure node hover is enabled
});

/**
 * CSS styles for Sigma.js labels and pill-shaped hover effects
 */
export const getLabelStyles = () => `
  .sigma-labels {
    pointer-events: none;
  }
  .sigma-label {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
    font-weight: 500 !important;
    fill: #000 !important;
    stroke: #fff !important;
    stroke-width: 2px !important;
    font-size: 14px !important;
    transition: all 0.2s ease-in-out;
  }
  .sigma-label:hover {
    fill: #000 !important;
  }
  .sigma-container .sigma-label {
    fill: #000 !important;
  }
  /* Force all labels to stay black */
  .sigma-layer-labels text {
    fill: #000 !important;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
    font-weight: 500 !important;
  }
  .sigma-layer-labels text:hover {
    fill: #000 !important;
  }
  
  /* Pill-shaped hover effect styling */
  .sigma-scene {
    transition: all 0.15s ease-in-out;
  }
  
  /* Remove any default shadow effects */
  .sigma-container canvas {
    filter: none !important;
  }
  
  /* Enhance the circular pill effect */
  .sigma-node-circle {
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  /* Label background for pill effect */
  .sigma-hover-label-bg {
    border-radius: 12px !important;
    backdrop-filter: blur(4px);
  }
`;
